import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import qrcode from "qrcode-generator";
import { useNavigate } from "react-router-dom";
import { dataTrigger } from "@/analytics";
import type { TelegramWebLogin as Login } from "@/api/auth";
import { pollTelegramWebLogin, startTelegramWebLogin } from "@/api/auth";
import { useAuth } from "@/store/authStore";
import { openTelegramLink } from "@/utils/telegramLink";
import { Button } from "@/ui/Button/Button";
import cls from "./styles.module.scss";

type State =
  | { kind: "idle"; error?: string }
  | { kind: "starting" }
  | { kind: "waiting"; login: Login; opened?: boolean }
  | { kind: "expired" };

const POLL_MS = 2000;

function qrSvg(text: string): string {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
}

function untilTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

/**
 * «Войти через Telegram» на сайте: код и QR, человек подтверждает вход в мини-аппе
 * БКоннект в Telegram (по подписи Telegram), страница сама получает вход.
 */
export function TelegramWebLogin() {
  const navigate = useNavigate();
  const setTokens = useAuth((s) => s.setTokens);
  const [state, setState] = useState<State>({ kind: "idle" });
  const timer = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const poll = useCallback(
    (login: Login) => {
      timer.current = window.setTimeout(async () => {
        try {
          const r = await pollTelegramWebLogin(login);
          if (r.status === "ok" && r.access && r.refresh) {
            setTokens(r.access, r.refresh, "telegram");
            navigate("/");
            return;
          }
          if (r.status === "pending") {
            poll(login);
            return;
          }
          setState({ kind: "expired" });
        } catch {
          poll(login); // сеть моргнула — пробуем дальше, код всё равно сгорит сам
        }
      }, POLL_MS);
    },
    [navigate, setTokens]
  );

  const start = async () => {
    stop();
    setState({ kind: "starting" });
    try {
      const login = await startTelegramWebLogin();
      setState({ kind: "waiting", login });
      poll(login);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      setState({
        kind: "idle",
        error:
          status === 404
            ? "Вход через Telegram сейчас выключен. Войдите по email."
            : status === 429
              ? "Слишком много попыток. Подождите несколько минут."
              : "Не получилось начать вход. Попробуйте ещё раз.",
      });
    }
  };

  const cancel = () => {
    stop();
    setState({ kind: "idle" });
  };

  const login = state.kind === "waiting" ? state.login : null;
  const svg = useMemo(() => (login ? qrSvg(login.telegram_link) : ""), [login]);

  if (state.kind === "waiting" && login) {
    return (
      <div className={cls.tgBox}>
        <p className={cls.tgTitle}>Подтвердите вход в Telegram</p>
        {/* SVG собирает библиотека из нашей же ссылки t.me — без внешних данных */}
        <div className={cls.tgQr} dangerouslySetInnerHTML={{ __html: svg }} aria-label="QR-код для входа" />
        <p className={cls.tgCode}>
          Код <b>{login.code.slice(0, 3)} {login.code.slice(3)}</b>
        </p>
        <Button
          variant="primary"
          size="lg"
          leftIcon={<Send />}
          onClick={() => {
            if (openTelegramLink(login.telegram_link) === "app") setState({ kind: "waiting", login, opened: true });
          }}
          {...dataTrigger("bkn.web_login.open_telegram")}
        >
          Открыть Telegram
        </Button>
        {state.opened ? (
          <p className={cls.tgHint}>
            Telegram не открылся? Наведите камеру телефона на QR-код или{" "}
            <a
              className={cls.linkBtn}
              href={login.telegram_link}
              target="_blank"
              rel="noopener noreferrer"
              {...dataTrigger("bkn.web_login.open_browser")}
            >
              откройте ссылку в браузере
            </a>
            .
          </p>
        ) : null}
        <p className={cls.tgHint}>
          <Loader2 className={cls.tgSpinner} />
          Ждём подтверждения. Telegram на телефоне — наведите камеру на QR-код. Код действует до{" "}
          {untilTime(login.expires_at)}.
        </p>
        <button type="button" className={cls.linkBtn} onClick={cancel}>
          Отмена
        </button>
      </div>
    );
  }

  if (state.kind === "expired") {
    return (
      <div className={cls.tgBox}>
        <p className={cls.tgTitle}>Код устарел</p>
        <Button variant="primary" size="lg" leftIcon={<Send />} onClick={start} {...dataTrigger("bkn.web_login.retry")}>
          Получить новый код
        </Button>
      </div>
    );
  }

  return (
    <div className={cls.tgStart}>
      <Button
        variant="primary"
        size="lg"
        leftIcon={state.kind === "starting" ? <Loader2 className={cls.tgSpinner} /> : <Send />}
        disabled={state.kind === "starting"}
        onClick={start}
        {...dataTrigger("bkn.web_login.start")}
      >
        Войти через Telegram
      </Button>
      {state.kind === "idle" && state.error ? <p className={cls.error}>{state.error}</p> : null}
    </div>
  );
}
